import './styles.css'
import { render } from 'react-dom'
import { useRef, Suspense, useState, useEffect } from 'react'
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber'
import { OrbitControls, Text, useIntersect } from '@react-three/drei'
import { SimpleReverb } from './SimpleReverb'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import allColors from './Colors'

let damp = 1
let modes = [
  (t, c, r) => Math.cos(t * 1 + Math.sqrt(c * r * 0.05)) + .5,
  (t, c, r) => Math.cos(c / 3 + Math.cos(r / 5 + t * 0.1) * 4) * 2,
  (t, c, r) => Math.tan(Math.sin(r / 3 - t * 0.1) + Math.cos(c / 2 - t * 3)),
  (t, c, r) => r / 4 & (c - 8) / r + t * 1,
  (t, c, r) => r / 4 & (c - 8) / r + t * 0.02,
  (t, c, r) => Math.cos(2 * r / c ^ 2 + t),
  (t, c, r) => ((c * r) % (8 + Math.floor(t % 8))) > 6 ? 1 : -.5,
  (t, c, r) => [7, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + (0 * 2) * a) ? -a / 3 : b),
  (t, c, r) => [8, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + 0 * a) ? -a / 5 : b),
  (t, c, r) => [8, 5, 3, 1].reduce((a, b) => r > a + a / 2 * Math.cos(c / a + 0 * a) ? -a / 10 : b),
  (t, c, r) => ((c + (r + t * 9) ^ c - (r + t * 9)) % 13 & 1) - .15,
  (t, c, r) => ((c + (r + t * 2) ^ c - (r + t * 2)) % 13 & 1) - .01,
  (t, c, r) => ((c + (r + t * 2) ^ c - (r + t * 2)) % 19 & 1) - .01,
  (t, c, r) => Math.sin(c / 8 + t) % Math.sin(r + t),
  (t, c, r) => Math.sin(2 * Math.atan2(r - 8, c) + t),
  (t, c, r) => c < 200 ? ((Date.now() + '')[c] - 5) / 5 : 0,
  (t, c, r) => c < 200 ? ((Date.now() + '')[c] - 5) / 5 : 0,
  (t, c, r) => c < 200 ? ((Date.now() + '')[c] - 5) / 5 : 0,
  (t, c, r) => Math.sin(t) * 5 * Math.sin(r / 5) * Math.sin(c / 5) % 1,
  (t, c, r) => ((4 * 9 + c - r * Math.tan(4) ^ c * Math.tan(4) + r) & 8) - 4,
  (t, c, r) => Math.max(0, Math.sin(r ^ (c + 9 * t))),
]
class AfterBurn extends AfterimagePass {
  constructor() {
    super(1);
    console.log(this.shaderMaterial)
    this.shaderMaterial.uniforms.time = { value: 0 }
    this.shaderMaterial.uniforms.speed = { value: 0.000 }
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
        uniform float time;
        uniform float speed;
        uniform float damp;
        uniform sampler2D tOld;
        uniform sampler2D tNew;
        varying vec2 vUv;
        vec4 when_gt( vec4 x, float y ) {
            return max( sign( x - y ), 0.000000001 );
        }
        
        void main() {
            vec2 offset = vec2(0.0, speed);
            vec4 texelOld = texture2D( tOld, vUv - offset);
            vec4 texelNew = texture2D( tNew, vUv );
            texelOld *= damp * when_gt( texelOld, 0.0000000001 );
            gl_FragColor = max(texelNew, texelOld);
        }
      `
    }
  }
}


extend({ EffectComposer, RenderPass, AfterBurn })
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => {
    ref.current.uniforms.damp.value = damp
    composer.current.render()
    damp = 1
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
let bg = 'rgb(15, 14, 18)'
let ct = 0

export default function App() {
  const size = useWindowSize();
  const ref = useRef()
  useEffect(() => {
    c = 0
  },[size.height,size.width])
  return (
    <>
      <div
        key={size.height+size.width}
        id="main"
        onPointerUp={() => {
          fadeIn()
          gen()
          if (((audioContext.currentTime - ct) < 0.2)) {
            damp = 0
            c = 0
          }
          if(!visible) {
            c = 0
          }
          setTimeout(() => {
            flag = true
          }, 50)
        }}
        onPointerDown={() => {
          if (!audioStarted) {
            whiteNoise.start(0);
            audioStarted = true;
          }
          for (let f of filters) {
            (randI(10) > 7) && (f.frequency.linearRampToValueAtTime((4 + randI(400)) * 10, audioContext.currentTime + rand([0.1,0.2])))
          }
          ct = audioContext.currentTime
        }}

        onDoubleClick={() => {
          toggleFullScreen()
        }}
      >
        <Canvas flat={true} linear={true} dpr={[1, 2]} camera={{
          position: [0, 0, 200], fov: 22.5,
        }}
          gl={{ antialias: false }}
        >
          <color attach="background" args={[bg]} />
          <Grids />
          <Inside />
          <Tint />
          <OrbitControls enableDamping={true} dampingFactor={0.02} zoomSpeed={3} ref={ref} maxDistance={650} />
        </Canvas>
      </div>
      <div id="overlay" />
    </>
  )
}
let c = 0
let maxWidth = (3 + randI(8)) * 7
let currWidth = 0
let columns = []
let mod = rand([2, 2,2,2,2,2,3, 4])
let colorMod = new Array(mod).fill().map(() => rand(rand(allColors)))
let modesMod = new Array(mod).fill().map(() => randI(modes.length))

let gen = () => {
  modesMod = new Array(mod).fill().map(() => randI(modes.length))
  currWidth = 0
  columns = []
  while (currWidth < maxWidth) {
    let aWidth = Math.min(1 + randI(rand([2, 3, 2, 2, 3, 2, 2, 1, 2, 1, 2,1,2,10,20])), maxWidth - currWidth)
    columns.push({
      width: aWidth,
      x: currWidth,
      color: colorMod[columns.length % mod],
      mode: modesMod[columns.length % mod],
      timeOffset: randI(100) * 0.1,
      timeMult: 1 + randI(4),
      scaleY: 0.1 + (randI(4) * 0.2),
      scaleX: 0.1 + (randI(4) * 0.2),
      tri: rand(['sin', 'cos', 'tan'])
    })
    currWidth += aWidth
  }
}
gen()
let visible = true
let flag = true
function Inside() {
  const ref = useIntersect((isVisible) => (visible = isVisible))
  useFrame((e) => {
    ref.current.position.y = c * 0.15
    if (!visible && flag) {
      fadeOut()
      flag = false
    }
  })

  return (
    <mesh ref={ref} scale={0.1}>
    </mesh>
  )
}
let start = false
let ch = 92+randI(5)
let gr = randI(100)>98 ? 0.5 : 0
function Grids() {
  const ref = useRef()
  setTimeout(() => {
    start = true
  }, 250)
  useFrame((e) => {
    if (!start) {
      return;
    }
    c += 0.75;
    if (randI(100) > ch) {
      let rc = rand(columns)
      rc.mode = randI(modes.length)
      rc.color = rand(rand(allColors))
    }
  })
  return (
    <group ref={ref} rotation={[0, 0, Math.PI * gr]}>
      <group position={[-currWidth * 0.5, 0, 0]}>
        {columns.map((c, i) =>
          <group key={i} position={[c.x, 0, 0]}>
            <Thang i={i} />
          </group>
        )}
        <group rotation={[Math.PI, 0, 0]}>
          {columns.map((c, i) =>
            <group key={i} position={[c.x, 0, 0]}>
              <Thang i={i} inv={true} />
            </group>
          )}
        </group>
      </group>
    </group>
  )
}

let align = rand(['left','center'])
function Thang({ i, inv }) {
  const ref = useRef();
  const ref2 = useRef();
  let col = columns[i]
  let text = ''
  useFrame(() => {
    text = ''
    for (let j = 0; j < col.width * 10; j++) {
      text += modes[col.mode](c * col.scaleX, (j + 10) * col.scaleY * (inv ? 1 : -1), col.timeOffset + (Math[col.tri](c * 0.08) * (inv ? 1 : -1)) * col.timeMult) > 0 ? '.' : ' '
    }
    ref.current.position.y = c * 0.15
    ref2.current.text = text
    ref2.current.color = col.color
  })
  return (
    <group ref={ref} >
      <Text
        ref={ref2}
        fontSize={1}
        letterSpacing={-0.5}
        characters={'.'}
        font={'./IBMPlexMono-ExtraLight.ttf'}
        anchorX={align}
        anchorY={'bottom-baseline'}
      >
        {text}
      </Text>
    </group>
  )
}

render(
  <Suspense fallback={null}><App /></Suspense>,
  document.getElementById('root')
)

window.AudioContext = window.AudioContext || window.webkitAudioContext;
let audioContext = new AudioContext();
let audioStarted = false;
let bufferSize = 2 * audioContext.sampleRate,
  noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate),
  output = noiseBuffer.getChannelData(0);
for (let i = 0; i < bufferSize; i++) {
  output[i] = Math.random() * 2 - 1;
}

let whiteNoise = audioContext.createBufferSource();
whiteNoise.buffer = noiseBuffer;
whiteNoise.loop = true;

let reverb = SimpleReverb(audioContext);
reverb.connect(audioContext.destination);
reverb.time = 4
reverb.wet.value = 0.9
reverb.dry.value = rand([0.01, 0.015])
reverb.filterType = 'lowpass'
reverb.cutoff.value = 300

let NUM = 8;
let filters = [];
let fgains = [];

for (let i = 0; i < NUM; i++) {
  let f = audioContext.createBiquadFilter();
  f.type = "bandpass";
  f.Q.value = 200;
  filters.push(f);
  let g = audioContext.createGain();
  g.gain.value = 0;
  fgains.push(g);
}
for (let i = 0; i < NUM; i++) {
  whiteNoise.connect(filters[i]);
  filters[i].connect(fgains[i]);
  fgains[i].connect(reverb);
}

function fadeIn() {
  fgains.map((_, i) => {
    fgains[i].gain.linearRampToValueAtTime(1.5, audioContext.currentTime + 3)
  })
  for (let f of filters) {
    (randI(10) > 6) && (f.frequency.linearRampToValueAtTime((4 + randI(400)) * 10, audioContext.currentTime + rand([0.15,0.2, 0.3, 0.4,0.5,0.6])))
  }
}
function fadeOut() {
  fgains.map((_, i) => {
    fgains[i].gain.linearRampToValueAtTime(0.15, audioContext.currentTime + (8 + randI(20)))
  })
}
function useWindowSize() { let [e, i] = useState({ width: void 0, height: void 0 }); return useEffect(() => { function e() { i({ width: window.innerWidth, height: window.innerHeight }) } return window.addEventListener("resize", e), e(), () => window.removeEventListener("resize", e) }, []), e }
function toggleFullScreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  } else if (document.exitFullscreen) {
    document.exitFullscreen();
  }
}

console.log('%c 🖨️🖨️ @ramioso 2023-02-03 🖨️🖨️', 'color: orange');
